import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { fetchTableSheetShareLink } from "../../api/tableBookingApi";
import { unwrapTableSheetShareLink } from "../../helpers/tableSheet";
import { showToast } from "../../store/features/toast/toastSlice";

/**
 * Copies the manager share URL for the public live table sheet.
 * @param {{ clubId: string, className?: string, size?: "sm" | "md" }} props
 */
const ShareTableSheetButton = ({ clubId, className = "", size = "sm" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [copying, setCopying] = useState(false);

  const onCopy = useCallback(async () => {
    const id = String(clubId || "").trim();
    if (!id || copying) return;
    setCopying(true);
    try {
      const raw = await fetchTableSheetShareLink(id);
      const link = unwrapTableSheetShareLink(raw);
      const url = String(link?.shareUrl || "").trim();
      if (!url) {
        throw new Error(t("TABLE_SHEET_SHARE_LINK_ERROR"));
      }
      await navigator.clipboard.writeText(url);
      dispatch(
        showToast({
          message: t("TABLE_SHEET_LINK_COPIED"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("TABLE_SHEET_SHARE_LINK_ERROR"),
          type: "error",
        })
      );
    } finally {
      setCopying(false);
    }
  }, [clubId, copying, dispatch, t]);

  if (!clubId) return null;

  const btnClass =
    size === "md"
      ? "btn btn-outline-primary"
      : "btn btn-outline-secondary btn-sm";

  return (
    <button
      type="button"
      className={`${btnClass} table-sheet-share-btn ${className}`.trim()}
      onClick={onCopy}
      disabled={copying}
    >
      <i className="bx bx-link-alt me-1" aria-hidden />
      {copying ? t("TABLE_SHEET_COPYING") : t("TABLE_SHEET_SHARE_BUTTON")}
    </button>
  );
};

export default ShareTableSheetButton;
